/**
 * <HorizontalSelectInput> — labeled horizontal option row (molecule, L0.1).
 *
 * Thin layout: section label (same typography as `<TextInput>` labels) + `<HorizontalList>`.
 * Selection / press live in the option chrome (`renderItem`) — this molecule does not own
 * `selectedId` / `onSelect`. `getItemId` is only used for React keys.
 */
import React from "react"

import { HorizontalList } from "../../atoms/HorizontalList"
import { Stack } from "../../atoms/containers/Stack"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { Text } from "../../atoms/typography/Text"
import { type SpacingKey } from "../../theme/tokens/spacing"

export type HorizontalSelectInputProps<T> = {
    /** Section label above the row — same style as `<TextInput>` labels. */
    label?: string
    items: T[]
    /** Stable key for each option. */
    getItemId: (item: T) => string | number
    renderItem: (item: T) => React.ReactNode
    gap?: SpacingKey
    /** Ce qui manque ici, dit en rouge sous le libellé. Absent → rien. */
    error?: string
    testID?: string
}

export function HorizontalSelectInput<T>(props: HorizontalSelectInputProps<T>) {
    const { label, items, getItemId, renderItem, gap = "2", error, testID } = props

    return (
        <Stack gap="3" testID={testID}>
            {label != null && (
                <Text size="text-base" weight="bold" color="primary">
                    {label}
                </Text>
            )}
            {error != null && error !== "" && <ErrorText live>{error}</ErrorText>}
            <HorizontalList gap={gap}>
                {items.map((item) => (
                    <React.Fragment key={String(getItemId(item))}>{renderItem(item)}</React.Fragment>
                ))}
            </HorizontalList>
        </Stack>
    )
}
